/**
 * TA-1324 (Kabir, typed 2026-10-13: "a easy entrance page that flows into the main page like a welcome mike and
 * then it goes in i have to press enter than a slow transition"). The heading the welcome screen shows, from the
 * signed-in person's display name: the first word of it, so "Mike" and "Mike Nekta" both read "Welcome, Mike."
 * An empty name reads "Welcome." and never "Welcome, ."
 */
export function welcomeHeading(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0] ?? "";
  return first === "" ? "Welcome." : `Welcome, ${first}.`;
}

/**
 * The sessionStorage key that remembers the named welcome was passed. sessionStorage belongs to one tab, so it
 * plays once per tab: a new tab or window greets again.
 */
export const WELCOME_SEEN_KEY = "ackra.welcome.seen";

/** What the welcome screen says: a heading and the line under it. */
export interface WelcomeWords {
  readonly heading: string;
  readonly lead: string;
  /** The sessionStorage key that remembers this greeting was passed. */
  readonly seenKey: string;
}

/**
 * The signed-out welcome's own memory (TA-1324 review): with one shared key, passing the plain welcome on /login
 * silenced the named one after sign-in, so nobody who signed in was ever greeted by name.
 */
export const WELCOME_SIGNED_OUT_SEEN_KEY = "ackra.welcome.signedout.seen";

/**
 * Whether the welcome appears once the page is live (TA-1324 review): never again after it was passed, and never
 * over a field the person is already typing in (a slow page they started using before it finished loading, or the
 * command palette opened first), because the veil would hide what they type and take their Enter.
 */
export function welcomeStartState({ seen, typing }: { readonly seen: boolean; readonly typing: boolean }): "shown" | "gone" {
  return seen || typing ? "gone" : "shown";
}

/** What counts as a field the person is typing in (TA-1324), read when the welcome starts and while it shows. */
export const WELCOME_FIELD_SELECTOR = "input, textarea, select, [contenteditable='true']";

/**
 * A field taking focus while the welcome shows means the person has moved on, so it closes and holds nothing
 * (TA-1353, the TA-1352 review). The welcome can start just after the palette opened; the palette's autofocus then
 * closes it here, before an Enter typed into the palette could be held as the welcome's.
 */
export function welcomeFocusIn(target: unknown, leave: () => void): void {
  const matches = (target as { matches?: unknown } | null)?.matches;
  if (typeof matches === "function" && matches.call(target, WELCOME_FIELD_SELECTOR) === true) leave();
}

/**
 * Any key closes the welcome (TA-1324 review). Enter closes it and goes no further: on the sign-in page a form sits
 * under the veil, and an Enter meant for the welcome must never submit it. Any other key goes on to the page, so a
 * person who starts typing or presses Cmd-K is not typing into something hidden behind the veil.
 */
export function welcomeKeyDown(
  e: { readonly key: string; preventDefault(): void; stopPropagation(): void },
  leave: () => void,
): void {
  if (e.key !== "Enter") {
    leave();
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  leave();
}

/** Signed in, on the overview: the person by first name, over the shop line. */
export function signedInWelcome(displayName: string): WelcomeWords {
  return { heading: welcomeHeading(displayName), lead: "Your shop, and what is waiting on you, is ready.", seenKey: WELCOME_SEEN_KEY };
}

/**
 * TA-1324 (Kabir, typed 2026-10-13: "it shoudl start as soon as i open the site"): signed out, the site opens on
 * the sign-in page or an invite link, and a stranger may be the one opening it, so it names nobody and says
 * nothing about the shop.
 */
export const SIGNED_OUT_WELCOME: WelcomeWords = {
  heading: "Welcome.",
  lead: "Sign in and your shop is ready for you.",
  seenKey: WELCOME_SIGNED_OUT_SEEN_KEY,
};
